import { DataTableStoreState } from "pages/gdt/store/DataTableProvider.types";
import { Dispatch, SetStateAction } from "react";
import { ProjectTablesTableColumns } from "./ProjectTablesTable";
import { isDefined } from "utils/isDefined";
import { ProjectFolderDjango } from "pages/gdt/folders/ProjectFoldersProvider.api";
import { ProjectDataTable } from "./ProjectTablesTable.types";

export const DEFAULT_FOLDER_COLOR = "#343741";

/**
 * Returns a sorted and filtered list of project tables
 *
 * @param tables - project tables
 * @param searchValue - string used to filter tables by name or key
 * @param filterTableTypeMap - map of types (analysis/data) to display
 * @param sortedColumn - tuple specifying the column to sort by and sort direction
 * @returns sorted and filtered project tables
 *
 * The function performs the following steps:
 * 1. Adds a `date_updated` field to each table, using the latest task date or the table's creation date.
 * 2. Filters tables based on the selected table types in `filterTableTypeMap`.
 * 3. Applies a text search filter to the table's name and key.
 * 4. Sorts tables alphabetically by name as a secondary sort criterion.
 * 5. Sorts tables based on the specified column and sort direction, handling date and other data types.
 */
export const getSortedAndFilteredTables = (
  tables: DataTableStoreState["tables"][number][],
  folderIds: string[],
  searchValue: string,
  filterTableTypeMap: Record<string, boolean>,
  sortedColumn: [string, "ASC" | "DESC"],
  showHidden: boolean,
): ProjectDataTable[] =>
  tables
    // add latest updated date
    // @TODO upcoming PR will support data tables
    .map((table) => {
      const latestDate = getLatestTaskDateFromTableRows(table);
      // filter out folder IDs that do not exist (ie. deleted in this session)
      const folder =
        table.folder !== null && folderIds.includes(table.folder)
          ? table.folder
          : null;
      return {
        ...table,
        date_updated: latestDate ? latestDate : table.date_created,
        folder,
      };
    })
    // filter out hidden tables if not showing hidden
    .filter((table) => {
      return showHidden ? true : !table.hidden;
    })

    // filter by table kind if selected
    .filter((table) => {
      const isToggleSelected =
        filterTableTypeMap.data || filterTableTypeMap.analysis;
      // none selected is interpreted as view all
      if (!isToggleSelected) {
        return true;
      }
      // filter data tables
      else if (filterTableTypeMap.data && table.kind === "data") return true;
      // filter analysis tables
      else if (filterTableTypeMap.analysis && table.kind === "analysis") {
        return true;
      } else return false;
    })
    // apply text search filters to name and key
    .filter((table) => {
      const tableName = table.name.toLowerCase();
      const tableKey = table.key.toLowerCase();
      const searchValueLower = searchValue.toLowerCase();
      return (
        tableName.includes(searchValueLower) ||
        tableKey.includes(searchValueLower)
      );
    })

    // sort alphabetical by name ascending first
    // this makes it so ties in the next sort are sorted alphabetically
    .sort((a, b) => a.name.localeCompare(b.name))
    .sort((a, b) => {
      const column = ProjectTablesTableColumns.find(
        (col) => col.id === sortedColumn[0],
      );

      const aValue = a[sortedColumn[0] as keyof ProjectDataTable];
      const bValue = b[sortedColumn[0] as keyof ProjectDataTable];

      // handle date sorting
      if (column?.sortType === "date") {
        const aDate = isDefined(aValue)
          ? new Date(aValue as string | number | Date)
          : new Date(0);
        const bDate = isDefined(bValue)
          ? new Date(bValue as string | number | Date)
          : new Date(0);
        if (aDate < bDate) {
          return sortedColumn[1] === "ASC" ? -1 : 1;
        }
        if (aDate > bDate) {
          return sortedColumn[1] === "ASC" ? 1 : -1;
        }
        return 0;
      }

      // handle other sorts
      if ((aValue ?? "") < (bValue ?? "")) {
        return sortedColumn[1] === "ASC" ? -1 : 1;
      }
      if ((aValue ?? "") > (bValue ?? "")) {
        return sortedColumn[1] === "ASC" ? 1 : -1;
      }
      return 0;
    });

/**
 * Returns a sorted and filtered list of tables in display order,
 * taking into consideration folder displaying first and being sorted
 * ascending or descending. Filters out tables from collapsed folders.
 *
 * Required to support the range selection feature
 *
 * @param sortedAndFilteredTables
 * @param folders
 * @param foldersExpandedMap
 * @param sortedColumn
 *
 * @returns A flat list of tables in displayed order
 */
export const getSortedAndFilteredTablesInDisplayOrder = (
  sortedAndFilteredTables: DataTableStoreState["tables"][number][],
  folders: ProjectFolderDjango[],
  foldersExpandedMap: Record<string, boolean>,
  sortedColumn: [string, "ASC" | "DESC"],
) => {
  const tablesInDisplayOrder = [];
  const sortedFolders = [...folders]
    // filter collapsed folders
    // assume folders not in the map are open
    .filter((folder) =>
      Object.prototype.hasOwnProperty.call(foldersExpandedMap, folder.id)
        ? foldersExpandedMap[folder.id]
        : true,
    )
    // respect sort order of folders
    // folders will sort by name alpabetically in the direction of
    // the sort column no matter what the sort column is
    .sort((a, b) => {
      const aName = a.name.toLowerCase();
      const bName = b.name.toLowerCase();
      if (aName < bName) {
        return sortedColumn[0] === "name" && sortedColumn[1] === "DESC"
          ? 1
          : -1;
      }
      if (aName > bName) {
        return sortedColumn[0] === "name" && sortedColumn[1] === "DESC"
          ? -1
          : 1;
      }
      return 0;
    });
  // folders always display first, so those tables go first
  for (const folder of sortedFolders) {
    const folderTables = sortedAndFilteredTables.filter(
      (table) => table.folder === folder.id,
    );
    if (folderTables.length > 0) {
      tablesInDisplayOrder.push(...folderTables);
    }
  }
  // then tables with no folder
  const tablesWithNoFolder = sortedAndFilteredTables.filter(
    (table) => table.folder === null,
  );
  tablesInDisplayOrder.push(...tablesWithNoFolder);
  return tablesInDisplayOrder;
};

/**
 * Selects a range of tables between the last table checked and a newly selected table.
 *
 * This function is used to enable range selection of tables when a user holds down
 * a modifier key (e.g., Shift) and selects a table. It selects all tables between
 * the last table that was checked and the currently selected table.
 *
 * @param tableId - ID of the table that was just selected
 * @param tables - SORTED AND FILTERED list of all currently displayed tables
 * @param lastTableChecked - ID of the last table that was checked. If null, no range selection is performed.
 * @param selectedTableMap - table ID to selection map
 * @param setSelectedTableMap
 */
export const selectRangeOfTables = (
  tableId: string,
  tables: DataTableStoreState["tables"][number][],
  lastTableChecked: string | null,
  selectedTableMap: Record<string, boolean>,
  setSelectedTableMap: Dispatch<SetStateAction<Record<string, boolean>>>,
) => {
  // only applies if a table is already selected
  if (lastTableChecked == null) {
    return;
  }
  const sortedAndFilteredTableIds = [...tables.map((table) => table.id)];
  const startIndex = sortedAndFilteredTableIds.indexOf(lastTableChecked);
  const endIndex = sortedAndFilteredTableIds.indexOf(tableId);
  const range =
    startIndex < endIndex
      ? sortedAndFilteredTableIds.slice(startIndex, endIndex + 1)
      : sortedAndFilteredTableIds.slice(endIndex, startIndex + 1);
  const newSelectedTableMap = { ...selectedTableMap };
  range.forEach((id) => {
    newSelectedTableMap[id] = true;
  });
  setSelectedTableMap(newSelectedTableMap);
};

/**
 * Get latest task date to represent "last updated" for analysis tables
 *
 * @param table - analysis table
 * @returns The most recent task creation date or null if no valid dates are found.
 */
export const getLatestTaskDateFromTableRows = (
  table: DataTableStoreState["tables"][number],
) => {
  // Flatten all `createdDate` values from tasks in all rows
  const allDates = table.rows
    .flatMap((row) =>
      "task" in row && row.task !== null
        ? new Date(row.task.date_created)
        : null,
    )
    .filter((date) => date !== null && !isNaN(date.getTime())); // Filter out invalid dates

  // Find the most recent date
  const latestDate =
    allDates.length > 0
      ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        new Date(Math.max(...allDates.map((date) => date!.getTime())))
      : null;

  return latestDate;
};
